export type TokenType = 'plain' | 'comment' | 'string' | 'keyword' | 'number' | 'variable' | 'flag' | 'command' | 'property';

export interface Token {
  text: string;
  type: TokenType;
}

export type Language = 'bash' | 'python' | 'json';

type Rule = [TokenType, RegExp];

// Ordered: the first rule that matches at the current position wins. Every regexp is sticky so it only matches there.
const RULES: Record<Language, Rule[]> = {
  bash: [
    ['comment', /#.*/y],
    ['string', /"(?:[^"\\]|\\.)*"|'[^']*'/y],
    ['variable', /\$\{?[A-Za-z_][A-Za-z0-9_]*\}?/y],
    ['command', /\bcurl\b/y],
    ['flag', /(?<=\s)--?[A-Za-z][\w-]*/y],
    ['number', /\b\d+\b/y],
  ],
  python: [
    ['comment', /#.*/y],
    ['string', /[fbr]?"(?:[^"\\\n]|\\.)*"|[fbr]?'(?:[^'\\\n]|\\.)*'/y],
    [
      'keyword',
      /\b(?:import|from|def|return|for|in|if|elif|else|try|except|raise|with|as|while|continue|break|not|and|or|is|None|True|False|class|pass|lambda)\b/y,
    ],
    ['number', /\b\d+(?:\.\d+)?\b/y],
  ],
  json: [
    ['property', /"(?:[^"\\]|\\.)*"(?=\s*:)/y],
    ['string', /"(?:[^"\\]|\\.)*"/y],
    ['number', /-?\b\d+(?:\.\d+)?\b/y],
    ['keyword', /\b(?:true|false|null)\b/y],
  ],
};

/** Splits code into tokens for colouring. Small and dependency-free: enough for the curl, Python and JSON samples of the guides. */
export const highlight = (code: string, language: Language): Token[] => {
  const rules = RULES[language];
  const tokens: Token[] = [];
  let plain = '';
  let index = 0;

  const flushPlain = () => {
    if (plain) tokens.push({ text: plain, type: 'plain' });
    plain = '';
  };

  while (index < code.length) {
    let matched: Token | null = null;

    for (const [type, pattern] of rules) {
      pattern.lastIndex = index;
      const match = pattern.exec(code);
      if (match && match[0].length > 0) {
        matched = { text: match[0], type };
        break;
      }
    }

    if (matched) {
      flushPlain();
      tokens.push(matched);
      index += matched.text.length;
    } else {
      plain += code[index];
      index += 1;
    }
  }

  flushPlain();
  return tokens;
};
